/**
 * Shared crash-screen content for every error boundary in the app
 * (top-level `ErrorBoundary`, per-route `RouteErrorBoundary`, and Layout's
 * `ChunkErrorBoundary`). Previously each boundary hand-rolled its own markup
 * and diverged — two of them styled buttons with `var(--accent)` / `var(--text)`,
 * CSS custom properties that are never defined anywhere in index.css, so the
 * buttons rendered with a transparent/`unset` background and were invisible
 * (worst possible failure mode: the recovery UI for a crash was itself broken).
 *
 * WHY LITERAL COLOR FALLBACKS ON EVERY var(): a crash can happen before the
 * app's CSS has finished loading (e.g. a chunk-load failure mid-deploy, or a
 * boundary catching an error during initial paint), so every custom property
 * used here carries a literal fallback — `var(--text-primary, #321d10)` — so
 * the screen still renders correctly with zero CSS loaded, in both the light
 * and dark literal values baked in below.
 *
 * WHY NO FRAMER MOTION / other heavy deps: this is the last line of defense
 * when something else in the app has already broken, so it must not depend
 * on libraries that could themselves be implicated in the crash (e.g. a
 * chunk-load failure while importing framer-motion for a lazy route).
 *
 * WHY HARDCODED ENGLISH: i18next itself may be uninitialized or broken when
 * a boundary renders (it's part of the app that could be crashing), so this
 * is the one sanctioned exception to the "everything goes through i18next"
 * rule (see AGENTS.md i18n guardrail) — a crash screen must never depend on
 * the thing that might have crashed.
 */

export interface ErrorFallbackAction {
  label: string;
  onClick: () => void;
  /** primary = filled action button (the main recovery action); secondary = outlined */
  variant?: 'primary' | 'secondary';
}

export interface ErrorFallbackProps {
  title: string;
  message: string;
  actions: ErrorFallbackAction[];
}

export function ErrorFallback({ title, message, actions }: ErrorFallbackProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        textAlign: 'center',
        // Self-contained card surface: the text fallbacks below are dark ink,
        // and in dark mode the pre-paint script (index.html) sets a dark html
        // background even before CSS loads — without an own light surface the
        // crash text would render dark-on-dark. In themed (post-CSS) renders
        // --bg-secondary wins, so the card follows Hearth/Dusk normally.
        backgroundColor: 'var(--bg-secondary, #fdf6ea)',
        border: '1px solid var(--border, #e5d5bd)',
        borderRadius: '16px',
        margin: '1.5rem auto',
        maxWidth: '420px',
      }}
    >
      <h2
        style={{
          fontSize: '1.125rem',
          fontWeight: 600,
          color: 'var(--text-primary, #321d10)',
          marginBottom: '0.5rem',
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: '0.9rem',
          color: 'var(--text-secondary, #624427)',
          marginBottom: '1.5rem',
          maxWidth: '360px',
          lineHeight: 1.5,
        }}
      >
        {message}
      </p>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {actions.map((action) =>
          action.variant === 'secondary' ? (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              style={{
                padding: '0.625rem 1.5rem',
                borderRadius: '0.5rem',
                backgroundColor: 'transparent',
                color: 'var(--action, #bc4929)',
                border: '1px solid var(--action, #bc4929)',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {action.label}
            </button>
          ) : (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              style={{
                padding: '0.625rem 1.5rem',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--action, #bc4929)',
                color: '#fff',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {action.label}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
